/**
 * Drawing Service
 * 
 * Full-screen drawing mode with stroke-based storage and IndexedDB persistence.
 * 
 * Architecture:
 * - Stroke-based format (not pure vector, not pure raster)
 * - Delta encoding for efficient storage
 * - IndexedDB for persistence
 * - Input handling with UI hit-testing priority
 */

import { bus } from '../../core/events.js';
import { log } from '../logging/index.js';

/**
 * Drawing tool type
 */
export type DrawingTool = 'pen' | 'brush' | 'eraser';

/**
 * Drawing point (with pressure and time)
 */
export interface DrawingPoint {
  x: number;
  y: number;
  pressure: number; // 0-1
  time: number; // timestamp
}

/**
 * Drawing stroke metadata
 */
export interface DrawingStrokeMetadata {
  source?: 'user' | 'ai';
  persona?: string;
  intent?: string;
  explanation?: string;
  confidence?: number;
}

/**
 * Drawing stroke
 */
export interface DrawingStroke {
  id: string;
  tool: DrawingTool;
  color: string;
  width: number;
  points: DrawingPoint[];
  metadata?: DrawingStrokeMetadata;
  opacity?: number; // 0-1, for helper strokes
}

/**
 * Drawing state
 */
export interface DrawingState {
  active: boolean;
  currentStroke: DrawingStroke | null;
  strokes: DrawingStroke[];
  tool: DrawingTool;
  color: string;
  width: number;
  isDrawing: boolean;
  lastPoint: DrawingPoint | null;
}

/**
 * Create drawing state
 */
export function createDrawingState(): DrawingState {
  return {
    active: false,
    currentStroke: null,
    strokes: [],
    tool: 'pen',
    color: '#1a1a1a', // Charcoal (dark) for left click
    width: 5, // Thicker lines
    isDrawing: false,
    lastPoint: null,
  };
}

/**
 * Drawing service state
 */
let state: DrawingState = createDrawingState();
let backgroundCanvas: HTMLCanvasElement | null = null;
let backgroundCtx: CanvasRenderingContext2D | null = null;
let db: IDBDatabase | null = null;

/**
 * Initialize IndexedDB for drawing persistence
 */
async function initializeIndexedDB(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const req = indexedDB.open('pivot-drawing', 1);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('strokes')) {
        db.createObjectStore('strokes', { keyPath: 'id' });
      }
    };

    req.onsuccess = () => {
      db = req.result;
      resolve();
    };

    req.onerror = () => {
      reject(req.error);
    };
  });
}

/**
 * Load strokes from IndexedDB
 */
async function loadStrokes(): Promise<void> {
  if (!db) {
    await initializeIndexedDB();
  }

  if (!db) return;

  return new Promise((resolve, reject) => {
    const tx = db!.transaction('strokes', 'readonly');
    const store = tx.objectStore('strokes');
    const req = store.getAll();

    req.onsuccess = () => {
      const encodedStrokes = req.result || [];
      // Decode strokes from delta encoding
      state.strokes = encodedStrokes.map((stroke: DrawingStroke) => decodeStroke(stroke));
      
      renderAllStrokes();
      resolve();
    };

    req.onerror = () => {
      reject(req.error);
    };
  });
}

/**
 * Save stroke to IndexedDB
 */
async function saveStroke(stroke: DrawingStroke): Promise<void> {
  if (!db) {
    await initializeIndexedDB();
  }

  if (!db) return;

  return new Promise((resolve, reject) => {
    const tx = db!.transaction('strokes', 'readwrite');
    const store = tx.objectStore('strokes');
    const req = store.put(stroke);

    req.onsuccess = () => {
      resolve();
    };

    req.onerror = () => {
      reject(req.error);
    };
  });
}

/**
 * Clear all strokes from IndexedDB
 */
async function clearStrokes(): Promise<void> {
  if (!db) {
    await initializeIndexedDB();
  }

  if (!db) return;

  return new Promise((resolve, reject) => {
    const tx = db!.transaction('strokes', 'readwrite');
    const store = tx.objectStore('strokes');
    const req = store.clear();

    req.onsuccess = () => {
      state.strokes = [];
      clearCanvas();
      resolve();
    };

    req.onerror = () => {
      reject(req.error);
    };
  });
}

/**
 * Initialize drawing service
 */
export async function initializeDrawing(
  canvas: HTMLCanvasElement
): Promise<void> {
  backgroundCanvas = canvas;
  backgroundCtx = canvas.getContext('2d');
  
  if (!backgroundCtx) {
    throw new Error('Failed to get 2D context for drawing canvas');
  }

  // Initialize IndexedDB
  try {
    await initializeIndexedDB();
    await loadStrokes();
  } catch (error) {
    log.error('Failed to initialize drawing IndexedDB', { error });
  }
}

/**
 * Activate drawing mode
 * Reloads strokes from IndexedDB when activated
 */
export async function activateDrawing(): Promise<void> {
  state.active = true;
  
  // Canvas setup is handled by BackgroundCanvasRenderer
  // We just need to ensure the context transform is correct
  if (backgroundCanvas && backgroundCtx) {
    // Get the DPR from the canvas (should match BackgroundCanvasRenderer)
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    // Reset transform and set DPR scale
    backgroundCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  
  // Reload strokes from IndexedDB when activating
  try {
    await loadStrokes();
  } catch (error) {
    log.error('Failed to load strokes when activating drawing', { error });
  }
  
  bus.emit('drawing.activated', {});
}

/**
 * Deactivate drawing mode
 */
export function deactivateDrawing(): void {
  state.active = false;
  state.isDrawing = false;
  state.currentStroke = null;
  state.lastPoint = null;
  bus.emit('drawing.deactivated', {});
}

/**
 * Set drawing tool
 */
export function setTool(tool: DrawingTool): void {
  state.tool = tool;
}

/**
 * Set drawing color
 */
export function setColor(color: string): void {
  state.color = color;
}

/**
 * Set drawing width
 */
export function setWidth(width: number): void {
  state.width = width;
}

/**
 * Clear canvas
 */
function clearCanvas(): void {
  if (!backgroundCtx || !backgroundCanvas) return;
  backgroundCtx.clearRect(0, 0, backgroundCanvas.width, backgroundCanvas.height);
}

/**
 * Render a single stroke
 */
function renderStroke(stroke: DrawingStroke): void {
  if (!backgroundCtx) return;

  if (stroke.points.length < 2) return;

  backgroundCtx.save();

  // Ensure transform is correct (background renderer might have reset it)
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  backgroundCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

  // Set stroke style based on tool
  if (stroke.tool === 'eraser') {
    backgroundCtx.globalCompositeOperation = 'destination-out';
    backgroundCtx.strokeStyle = 'rgba(0, 0, 0, 1)';
  } else {
    backgroundCtx.globalCompositeOperation = 'source-over';
    backgroundCtx.strokeStyle = stroke.color;
  }

  backgroundCtx.lineWidth = stroke.width;
  
  // Use square caps/joins for AI strokes (geometric precision)
  // Use round caps/joins for user strokes (smooth drawing)
  const isAIStroke = stroke.metadata?.source === 'ai';
  if (isAIStroke) {
    backgroundCtx.lineCap = 'square';
    backgroundCtx.lineJoin = 'miter';
  } else {
    backgroundCtx.lineCap = 'round';
    backgroundCtx.lineJoin = 'round';
  }
  
  // Apply opacity if specified (for AI helper strokes)
  const originalAlpha = backgroundCtx.globalAlpha;
  if (stroke.opacity !== undefined && stroke.opacity < 1.0) {
    backgroundCtx.globalAlpha = stroke.opacity;
  }

  // Draw stroke with pressure variation
  backgroundCtx.beginPath();
  
  // For AI strokes, use straight lines (geometric precision)
  // For user strokes, use curves (smooth drawing)
  
  for (let i = 0; i < stroke.points.length; i++) {
    const point = stroke.points[i];
    const pressure = point.pressure || 0.5;
    
    // Adjust line width based on pressure (only for user strokes)
    if (!isAIStroke) {
      const adjustedWidth = stroke.width * (0.5 + pressure * 0.5);
      backgroundCtx.lineWidth = adjustedWidth;
    }

    if (i === 0) {
      backgroundCtx.moveTo(point.x, point.y);
    } else if (isAIStroke) {
      // AI strokes: use straight lines for geometric precision
      backgroundCtx.lineTo(point.x, point.y);
    } else {
      // User strokes: use quadratic curves for smooth drawing
      const prevPoint = stroke.points[i - 1];
      const midX = (prevPoint.x + point.x) / 2;
      const midY = (prevPoint.y + point.y) / 2;
      
      if (i === 1) {
        backgroundCtx.lineTo(midX, midY);
      } else {
        backgroundCtx.quadraticCurveTo(prevPoint.x, prevPoint.y, midX, midY);
      }
      
      if (i === stroke.points.length - 1) {
        backgroundCtx.lineTo(point.x, point.y);
      }
    }
  }

  backgroundCtx.stroke();
  
  // Restore original alpha
  backgroundCtx.globalAlpha = originalAlpha;

  backgroundCtx.restore();
}

/**
 * Render all strokes (including current stroke being drawn)
 */
export function renderAllStrokes(): void {
  if (!backgroundCtx || !backgroundCanvas) return;

  // Don't clear canvas - preserve background
  // Just render strokes on top

  // Render all completed strokes
  for (const stroke of state.strokes) {
    renderStroke(stroke);
  }
  
  // Also render the current stroke being drawn (if any)
  if (state.currentStroke && state.currentStroke.points.length >= 2) {
    renderStroke(state.currentStroke);
  }
}

/**
 * Start drawing stroke
 */
export function startStroke(x: number, y: number, pressure: number = 0.5, isRightClick: boolean = false): void {
  if (!state.active) return;

  state.isDrawing = true;
  state.lastPoint = { x, y, pressure, time: Date.now() };

  // Determine color based on click type
  // Right click = white, left click = charcoal (black)
  const strokeColor = isRightClick ? '#ffffff' : state.color;

  state.currentStroke = {
    id: `stroke-${Date.now()}-${Math.random()}`,
    tool: state.tool,
    color: strokeColor,
    width: state.width,
    points: [state.lastPoint],
  };
}

/**
 * Continue drawing stroke
 */
export function continueStroke(x: number, y: number, pressure: number = 0.5, isRightClick: boolean = false): void {
  if (!state.active || !state.isDrawing || !state.currentStroke) return;

  const point: DrawingPoint = { x, y, pressure, time: Date.now() };
  state.currentStroke.points.push(point);

  // Render stroke incrementally for smooth feedback
  // Note: The background renderer will overwrite this, but renderAllStrokes() will re-render it
  if (state.currentStroke.points.length >= 2) {
    // Render just the new segment
    if (backgroundCtx) {
      backgroundCtx.save();

      // Ensure transform is correct (background renderer might have reset it)
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      backgroundCtx.setTransform(dpr, 0, 0, dpr, 0, 0);

      if (state.currentStroke.tool === 'eraser') {
        backgroundCtx.globalCompositeOperation = 'destination-out';
        backgroundCtx.strokeStyle = 'rgba(0, 0, 0, 1)';
      } else {
        backgroundCtx.globalCompositeOperation = 'source-over';
        backgroundCtx.strokeStyle = state.currentStroke.color;
      }

      const adjustedWidth = state.currentStroke.width * (0.5 + pressure * 0.5);
      backgroundCtx.lineWidth = adjustedWidth;
      backgroundCtx.lineCap = 'round';
      backgroundCtx.lineJoin = 'round';

      backgroundCtx.beginPath();
      
      if (state.lastPoint) {
        backgroundCtx.moveTo(state.lastPoint.x, state.lastPoint.y);
        backgroundCtx.lineTo(x, y);
      }

      backgroundCtx.stroke();
      backgroundCtx.restore();
    }
    
    // Trigger a render to ensure the current stroke is visible
    // The render loop will call renderAllStrokes() which includes the current stroke
    bus.emit('drawing.strokeUpdated', {});
  }

  state.lastPoint = point;
}

/**
 * End drawing stroke
 */
export async function endStroke(): Promise<void> {
  if (!state.active || !state.isDrawing || !state.currentStroke) return;

  // Only save strokes with at least 2 points
  if (state.currentStroke.points.length >= 2) {
    // Push original stroke to state.strokes (for rendering)
    // We need the original stroke with absolute coordinates for rendering
    state.strokes.push({ ...state.currentStroke });
    
    // Apply delta encoding for storage efficiency (only for IndexedDB)
    const encodedStroke = encodeStroke(state.currentStroke);
    
    // Save encoded stroke to IndexedDB
    try {
      await saveStroke(encodedStroke);
    } catch (error) {
      log.error('Failed to save stroke', { error });
    }
  }

  state.isDrawing = false;
  state.currentStroke = null;
  state.lastPoint = null;
}

/**
 * Encode stroke with delta encoding
 * First point is absolute, rest are deltas
 */
function encodeStroke(stroke: DrawingStroke): DrawingStroke {
  if (stroke.points.length < 2) return stroke;

  const encoded = {
    ...stroke,
    points: [stroke.points[0]], // First point is absolute
  };

  // Encode rest as deltas
  for (let i = 1; i < stroke.points.length; i++) {
    const prev = stroke.points[i - 1];
    const curr = stroke.points[i];
    
    encoded.points.push({
      x: curr.x - prev.x, // Delta
      y: curr.y - prev.y, // Delta
      pressure: curr.pressure,
      time: curr.time - prev.time, // Delta time
    });
  }

  return encoded;
}

/**
 * Decode stroke from delta encoding
 */
function decodeStroke(stroke: DrawingStroke): DrawingStroke {
  if (stroke.points.length < 2) return stroke;

  const decoded = {
    ...stroke,
    points: [stroke.points[0]], // First point is absolute
  };

  // Decode rest from deltas
  for (let i = 1; i < stroke.points.length; i++) {
    const prev = decoded.points[i - 1];
    const delta = stroke.points[i];
    
    decoded.points.push({
      x: prev.x + delta.x, // Reconstruct absolute
      y: prev.y + delta.y, // Reconstruct absolute
      pressure: delta.pressure,
      time: prev.time + delta.time, // Reconstruct absolute time
    });
  }

  return decoded;
}

/**
 * Get drawing state
 */
export function getDrawingState(): DrawingState {
  return { ...state };
}

/**
 * Clear all drawings
 */
export async function clearDrawing(): Promise<void> {
  await clearStrokes();
}

/**
 * Add AI-generated stroke to canvas
 * 
 * This function adds a stroke that was generated by AI (not drawn by user).
 * AI strokes are marked with metadata and can be rendered with different opacity.
 */
export async function addAIStroke(stroke: DrawingStroke): Promise<void> {
  // Ensure stroke has required fields
  if (!stroke.id) {
    stroke.id = `ai-stroke-${Date.now()}-${Math.random()}`;
  }
  
  // Mark as AI-generated if not already marked
  if (!stroke.metadata) {
    stroke.metadata = {};
  }
  stroke.metadata.source = 'ai';
  
  // Set default opacity for helper strokes (if not specified)
  if (stroke.opacity === undefined) {
    // Helper strokes (grids, guides) use lower opacity
    // Artistic strokes use full opacity
    stroke.opacity = stroke.metadata.intent?.includes('grid') || 
                     stroke.metadata.intent?.includes('guide') ||
                     stroke.metadata.intent?.includes('helper')
      ? 0.3 
      : 1.0;
  }
  
  // Check if stroke already exists (for animated drawing updates)
  const existingIndex = state.strokes.findIndex(s => s.id === stroke.id);
  const isNewStroke = existingIndex < 0;
  
  if (existingIndex >= 0) {
    // Replace existing stroke (during animation)
    state.strokes[existingIndex] = stroke;
  } else {
    // Add new stroke
    state.strokes.push(stroke);
  }
  
  // Re-render all strokes (to update the modified stroke)
  renderAllStrokes();
  
  // Save to IndexedDB only for new strokes (not during animation updates)
  if (isNewStroke && stroke.points.length >= 2) {
    try {
      // Apply delta encoding for storage efficiency
      const encodedStroke = encodeStroke(stroke);
      await saveStroke(encodedStroke);
      log.debug('Saved AI stroke to IndexedDB', { 
        id: encodedStroke.id, 
        points: encodedStroke.points.length 
      });
    } catch (error) {
      log.error('Failed to save AI stroke to IndexedDB', { error, strokeId: stroke.id });
    }
  }
  
  // Emit event
  bus.emit('drawing.aiStrokeAdded', { stroke });
}

/**
 * Remove a stroke by ID
 */
export function removeStroke(strokeId: string): void {
  const index = state.strokes.findIndex(s => s.id === strokeId);
  if (index >= 0) {
    state.strokes.splice(index, 1);
    // Re-render all strokes
    renderAllStrokes();
  }
}

